import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'wouter';
import type { SnapshotAction } from '@shared/v2-schemas';

import { cn } from '@/lib/utils';
import {
  useConversationContext,
  useConversationLifecycleContext,
  useConversationNavigation,
} from '@/contexts/v2/ConversationContext';
import { useProfileSettings } from '@/hooks/v2/useProfileSettings';
import { V2ConversationTranscript } from '@/components/v2/conversation/ConversationTranscript';
import { ChatActionsCarousel } from '@/components/v2/conversation/ChatActionsCarousel';
import { ConversationEndedState } from '@/components/v2/conversation/ConversationEndedState';
import { ConversationInputBar } from '@/components/v2/conversation/ConversationInputBar';
import { WiredConversationErrorNotice } from '@/components/v2/conversation/WiredConversationErrorNotice';
import { voiceFocusTurns } from '@/components/v2/conversation/voiceFocusTurns';
import {
  repairScopingArtifactWidgets,
  type ScopingArtifactFallback,
  type ScopingWidgetRepairSignal,
} from '@/components/v2/conversation/scoping/scopingWidgetRepair';
import { publishInjectUserTurn } from '@/components/v2/conversation/injectUserTurn';
import { TranscriptHydrateSkeleton } from '@/components/v2/conversation/TranscriptHydrateSkeleton';

export interface ChatThreadProps {
  /**
   * Forwarded for symmetry with the provider's mode prop. Unused at this
   * layer because `<ConversationProvider>` already forwards `mode` to
   * `useConversation`; accepted so future phases can drive per-instance mode
   * overrides without a breaking change.
   */
  mode?: string;
  /**
   * Shell mode (`'peek' | 'detailed' | 'focused'`) — passed through to
   * `<ConversationInputBar>` so the in-bar Maximize2/Minimize2 toggle
   * knows which icon to render and when to render nothing.
   *
   * NOTE: `ChatThread` only renders the full transcript. The shell now
   * uses this component exclusively for `'focused'` mode (and the mobile
   * Sheet variant of focused). Peek + detailed render via
   * `LastResponseCard` + `WiredConversationInputBar` directly in the
   * shell — no transcript, no widget carousel.
   */
  shellMode?: 'peek' | 'detailed' | 'focused';
  /** Flip Detailed ↔ Focused. Hidden in the bar if absent. */
  onToggleShellMode?: () => void;
  /**
   * Visual override for the chat thread surface. `'transparent'` removes
   * the default `var(--chat-surface)` background/border/shadow so bubbles
   * sit directly on the page — used in `'focused'` mode where the dashboard
   * is hidden and the thread anchors to the bottom of the viewport.
   */
  surface?: 'card' | 'transparent';
  /**
   * When true, calls `startSession()` exactly once on mount as long as the
   * underlying conversation hook is still in `phase === 'mounting'`. The
   * provider has its own `proactiveStart` knob; this prop lets a surface opt
   * in even when the provider was created with `proactiveStart={false}`.
   */
  proactiveStart?: boolean;
  /** When false, the input bars hide their expand affordance. */
  expandable?: boolean;
  expanded?: boolean;
  onToggleExpand?: () => void;
  /**
   * Voice-chrome positioning. `true` (default) renders the ACTIVE voice chrome as
   * a viewport-fixed overlay (the global-chat behaviour). `false` keeps it
   * IN-FLOW at the bottom of the thread column, so the thread reserves space for
   * it and transcript content never scrolls behind it. The scoping surface passes
   * `false` so switching between text and voice never pulls the bar out of the
   * thread, regardless of how voice was entered.
   */
  pinnedVoiceChrome?: boolean;
  className?: string;
  /** Snapshot action cards rendered above the input bar; carousel is hidden until `isReady`. */
  actions?: SnapshotAction[];
  /**
   * Override for chat_actions navigation (`kind: 'navigate'`). Defaults to wouter's
   * `setLocation`. No SSE turn is sent on navigation.
   */
  onNavigate?: (href: string) => void;
  onWidgetAction?: (args: {
    widget_id: string;
    action: string;
    text?: string;
  }) => void;
  /**
   * Scoping-only: known-good, rail-derived artifact cards keyed by kind. When the
   * mode is `scoping` and this is provided, malformed model-authored
   * `sc_artifact_card` widgets in the transcript are repaired against it (a
   * tactical guard ahead of spec 004-p6's Cloud materialisation). Omitted in every
   * other mode — the transcript is left untouched. See
   * {@link repairScopingArtifactWidgets}.
   */
  scopingWidgetFallback?: ScopingArtifactFallback;
  /** Fires once per emitted scoping artifact widget with the shape-match verdict. */
  onScopingWidgetSignal?: (signal: ScopingWidgetRepairSignal) => void;
  /**
   * Forwarded verbatim to the transcript — fires 'down'/'up' once the thread's
   * cumulative scroll movement crosses a small threshold. Lets a page-level
   * chrome (e.g. the scoping mobile nav) auto-hide/reveal in step with the
   * thread.
   */
  onScrollDirection?: (direction: 'up' | 'down') => void;
}

const noop = () => {};

/** Stable empty signal list — reused when the repair doesn't run, so the
 *  signal-emitting effect's dependency identity stays constant across renders. */
const EMPTY_REPAIR_SIGNALS: ScopingWidgetRepairSignal[] = [];

export function ChatThread({
  proactiveStart = false,
  expandable = true,
  expanded,
  onToggleExpand,
  pinnedVoiceChrome = true,
  className,
  actions = [],
  onNavigate,
  onWidgetAction,
  shellMode,
  onToggleShellMode,
  surface = 'card',
  scopingWidgetFallback,
  onScopingWidgetSignal,
  onScrollDirection,
}: ChatThreadProps) {
  const [pathname, setLocation] = useLocation();
  const navigate = onNavigate ?? setLocation;
  const lifecycle = useConversationLifecycleContext();
  const conversation = useConversationContext();
  const { requestContinuation } = useConversationNavigation();
  const { setMode } = useProfileSettings();
  const {
    phase,
    isReady,
    inputReady,
    isThinking,
    isResponding,
    textStatusUpdate,
    inputMode,
    setInputMode,
    turns,
    isHydratingTranscript,
    entryActions,
    sendMessage,
    startSession,
    voiceState,
    paused,
    pauseReason,
    pausedMs,
    voiceStartedAt,
    disconnectVoice,
    pauseVoice,
    resumeVoice,
    error,
    connectVoice,
    room,
    livekitUrl,
    micBlocked,
    micErrorKind,
    idlePrompting,
    sessionCapWarning,
    extendSession,
    lockScopeRequested,
    requestLockScope,
  } = conversation;

  // proactiveStart fires exactly once per ChatThread mount; the provider's
  // own `proactiveStart` covers the App-root path, this covers surfaces that
  // own startup themselves (e.g. the future home page).
  const proactiveStartedRef = useRef(false);
  useEffect(() => {
    if (!proactiveStart) return;
    if (proactiveStartedRef.current) return;
    if (phase !== 'mounting') return;
    proactiveStartedRef.current = true;
    void startSession();
  }, [proactiveStart, phase, startSession]);

  // T015 — navigate_and_send: replaced per-component ref with the singleton
  // ContinuationHandoff (pendingUserMessage). The handoff survives layout
  // transitions that previously dropped the local ref when ChatThread unmounted.
  const handleNavigateAndSend = useCallback(
    (href: string, text: string) => {
      if (pathname === href) {
        void sendMessage(text);
        return;
      }
      const convId = lifecycle.conversationId ?? '';
      requestContinuation({
        targetSurface: href,
        conversationId: convId,
        pendingUserMessage: text,
      });
      navigate(href);
    },
    [pathname, sendMessage, navigate, lifecycle.conversationId, requestContinuation],
  );

  const handleSwitchToVoice = useCallback(() => {
    setInputMode('voice');
    void connectVoice();
    void setMode('voice');
  }, [setInputMode, connectVoice, setMode]);

  const handleSwitchToText = useCallback(() => {
    setInputMode('text');
    void setMode('text');
  }, [setInputMode, setMode]);

  const handleStartVoiceFromIdle = useCallback(() => connectVoice(), [connectVoice]);

  // ANA-542 — "Lock the scope" tapped on the session-cap wind-down banner.
  // Mirrors handleTranscriptAction's voice-vs-text routing (039 phase 2 1b):
  // in a live voice session this must reach the agent via inject_user_turn,
  // not sendMessage's text-SSE path, or the voice bar never cycles.
  // `lockScopeRequested`/`requestLockScope` come from the shared conversation
  // hook (not component-local state) — see the `useVoiceConversation`
  // `lockScopeRequested` doc for why: switching shell mode between the tap
  // and its response landing would otherwise remount this component with a
  // fresh local guard, letting a second tap double-publish.
  const handleLockScope = useCallback(() => {
    if (lockScopeRequested) return;
    requestLockScope?.();
    const text = 'Lock the scope.';
    const publishedToVoice =
      inputMode === 'voice' && !!room?.localParticipant && publishInjectUserTurn(room, text);
    if (!publishedToVoice) {
      void sendMessage(text);
    }
  }, [lockScopeRequested, requestLockScope, inputMode, room, sendMessage]);

  const handleTranscriptAction = useCallback(
    (
      _widgetId: string,
      action: string,
      payload?: Record<string, unknown>,
      meta?: { text?: string; label?: string },
    ) => {
      if (
        action === 'recommendation_action' &&
        payload &&
        typeof payload.recommendation_id === 'string' &&
        (payload.decision === 'accept' || payload.decision === 'decline')
      ) {
        const decision = payload.decision;
        const label = decision === 'accept' ? 'Accepted recommendation' : 'Declined recommendation';
        void sendMessage(label, {
          clientStructured: {
            kind: 'recommendation_action',
            recommendation_id: payload.recommendation_id,
            action: decision,
          },
        });
        return;
      }

      // The user-turn text posted to the thread when a widget button is clicked.
      // Precedence: an explicit message carried in the action `payload` wins
      // ("payload is what should be shown"); otherwise fall back to the action's
      // synthetic `text`, then its button `label`. `label` is always present, so
      // every action button is live regardless of its id — the producer (guide)
      // owns the wording, no hardcoded per-id map.
      const payloadText =
        payload && typeof payload.text === 'string' ? payload.text.trim() : '';
      const fallbackText = (meta?.text ?? '').trim() || (meta?.label ?? '').trim();

      // Spec 034 — scoping capture/closure round-trip. The sc_* cards
      // (decision/bets/assumptions/scope artifact, choice, scope-summary) fire
      // their approve/adjust/add/confirm/lock/edit action through here; in
      // scoping it must reach the model as a turn so it captures-on-approve and,
      // on lock, closes per the v4.0 guide (lock -> end_session). The turn text
      // IS the model-facing approval signal. Scoping-gated so home/update/demo
      // are unaffected.
      if (lifecycle.mode === 'scoping') {
        // sc_choice_card.confirm carries the chosen option in `selected_id`
        // (e.g. the posture: validation | exploratory). Name it in the turn so
        // the model captures the right choice — payload-derived, so it ranks
        // with the payload message above the text/label fallback.
        const selectedId =
          payload && typeof payload.selected_id === 'string' ? payload.selected_id.trim() : '';
        const turnText =
          payloadText || (selectedId ? `Let's go with: ${selectedId}.` : '') || fallbackText;
        if (turnText) {
          // 039 phase 2 (1b) — in a LIVE voice session a widget action (e.g. the
          // scope Lock button) must reach the voice agent, not `sendMessage`'s
          // text-SSE path: the agent never speaks a text turn and it wouldn't
          // cycle the voice bar. Publish an `inject_user_turn` frame so the agent
          // runs the same machinery as a spoken "lock it" (and speaks the reply).
          // Fall back to `sendMessage` for text mode or if the publish fails.
          const publishedToVoice =
            inputMode === 'voice' && !!room?.localParticipant && publishInjectUserTurn(room, turnText);
          if (!publishedToVoice) {
            void sendMessage(turnText);
          }
          onWidgetAction?.({ widget_id: _widgetId, action, text: turnText });
          return;
        }
      }

      const turnText = payloadText || fallbackText;
      onWidgetAction?.({ widget_id: _widgetId, action, text: turnText || undefined });
    },
    [onWidgetAction, sendMessage, lifecycle.mode, inputMode, room],
  );

  const expandedProp = expandable ? expanded : undefined;
  const onToggleExpandProp = expandable ? onToggleExpand : undefined;

  // Spec 014 US1 — cold-entry pills. On a cold direct entry to update mode the
  // server emits entry pills with no assistant text turn (Anaka stays silent
  // until the user acts), so they cannot attach to a last assistant turn. They
  // arrive on `entryActions` and render here as a standalone outlined carousel
  // until the transcript gains an assistant turn (or the user acts, which clears
  // `entryActions` in the hook). Suppressed in voice mode and peek shell, mirroring
  // the per-turn pill gating in <V2ConversationTranscript>.
  const hasAssistantTurn = turns.some((t) => t.role === 'assistant');
  // ANA-294 / matrix B2 — next-action pills (turn_actions) are a home/update
  // affordance only; suppress them in scoping/demo/interview.
  const allowActionPills = lifecycle.mode === 'home' || lifecycle.mode === 'update';
  const showEntryActions =
    !lifecycle.isEnded &&
    !hasAssistantTurn &&
    entryActions.length > 0 &&
    inputMode !== 'voice' &&
    shellMode !== 'peek' &&
    allowActionPills;

  const isFocused = shellMode === 'focused';
  // Voice thread collapse (017) — driven by the CURRENT input mode, not how the
  // founder entered (ANA-343 decoupled): whenever voice is active the thread
  // shows only widget-carrying turns and dedups a re-emitted artifact of the
  // same kind to its latest version. Switching back to text restores the full
  // history (voiceFocusTurns is a render-time transform; `turns` is untouched).
  // Scoping resilience guard (tactical, ahead of 004-p6): repair malformed
  // model-authored artifact widgets against the rail-derived known-good cards
  // BEFORE the voice-focus transform, so voice dedupe keys off the repaired kind.
  // No-op in every other mode (or when no fallback is supplied). PURE — the
  // per-block signals are RETURNED and fired from an effect (below), never from
  // this render-phase memo.
  const { turns: repairedTurns, signals: repairSignals } = useMemo(
    () =>
      lifecycle.mode === 'scoping' && scopingWidgetFallback
        ? repairScopingArtifactWidgets(turns, scopingWidgetFallback)
        : { turns, signals: EMPTY_REPAIR_SIGNALS },
    [lifecycle.mode, scopingWidgetFallback, turns],
  );
  // Emit each widget's shape-match verdict ONCE (not once per render): dedupe by
  // (turnId, widgetId) across the thread's lifetime. The memo re-runs over every
  // turn whenever a new turn arrives, so without this dedupe an on-screen card
  // would re-signal on each turn/render (and twice under StrictMode).
  const signaledRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!onScopingWidgetSignal) return;
    for (const s of repairSignals) {
      const key = `${s.turnId} ${s.widgetId}`;
      if (signaledRef.current.has(key)) continue;
      signaledRef.current.add(key);
      onScopingWidgetSignal(s);
    }
  }, [repairSignals, onScopingWidgetSignal]);
  const transcriptTurns =
    inputMode === 'voice' ? voiceFocusTurns(repairedTurns) : repairedTurns;
  // Focused mode anchors the thread to the bottom with horizontal padding so
  // bubble shadows aren't clipped; the surface itself goes transparent so
  // the page background shows through.
  return (
    <div
      data-testid="chat-thread"
      data-phase={phase}
      data-shell-mode={shellMode}
      data-surface={surface}
      className={cn(
        'flex h-full min-h-0 flex-col',
        // Focused centres in an 820px column (handoff GlobalChat) so bubbles
        // don't sprawl across the full viewport width. `pb-3` keeps the input
        // bar off the window bottom, mirroring detailed mode's wrapper padding
        // (text mode's TextZone carries no bottom offset of its own).
        isFocused && 'mx-auto w-full max-w-[820px] justify-end px-3.5 pb-3',
        className,
      )}
    >
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {isHydratingTranscript && turns.length === 0 ? (
          // ANA-371 — resumed thread still hydrating (GET /:id/turns in flight) and
          // no turns yet: show a message skeleton instead of a blank thread until
          // restored history lands.
          <TranscriptHydrateSkeleton />
        ) : (
          <V2ConversationTranscript
            turns={transcriptTurns}
            isThinking={isThinking}
            isResponding={isResponding ?? false}
            textStatusUpdate={textStatusUpdate}
            onAction={handleTranscriptAction}
            assistantBubbleSurface={isFocused ? 'white' : 'glass'}
            onSendMessage={sendMessage}
            onNavigate={navigate}
            onNavigateAndSend={handleNavigateAndSend}
            onWidgetAction={onWidgetAction}
            inputMode={inputMode}
            shellMode={shellMode}
            allowActionPills={allowActionPills}
            onScrollDirection={onScrollDirection}
          />
        )}
      </div>

      {/* Text-turn failure (n8n error / timeout / empty) → inline, accessible
          error + Retry (ANA-194). Shared with peek/detailed via the wired
          container; self-gates to text mode (voice owns its own error chrome). */}
      <WiredConversationErrorNotice className="mx-4 mb-2" />


      {showEntryActions ? (
        <ChatActionsCarousel
          actions={entryActions}
          variant="outlined"
          onSendMessage={sendMessage}
          onNavigate={navigate}
          onNavigateAndSend={handleNavigateAndSend}
          onWidgetAction={onWidgetAction ?? noop}
        />
      ) : null}

      {!lifecycle.isEnded && isReady && actions.length > 0 && allowActionPills ? (
        <ChatActionsCarousel
          actions={actions}
          onSendMessage={sendMessage}
          onNavigate={navigate}
          onNavigateAndSend={handleNavigateAndSend}
          onWidgetAction={onWidgetAction ?? noop}
        />
      ) : null}

      {lifecycle.isEnded ? (
        lifecycle.endReason === 'guardrail' ? (
          // 011 F3 — hardened-guardrail termination: the refusal message is
          // already in the transcript above; close the conversation and route
          // the user back to home so they can start a fresh session.
          <ConversationEndedState
            reason="guardrail"
            onReset={() => {
              lifecycle.reset();
              navigate('/');
            }}
          />
        ) : lifecycle.endReason === 'complete' ||
          lifecycle.endReason === 'scope_locked' ? (
          // ANA-575 — Anaka intentionally + terminally ended the call (a voice
          // goodbye / scope-lock close). Non-scoping surface: show the "Anaka
          // ended the call" banner with a fresh-start CTA (scoping has its own
          // ScopingLockedState on the page). `cap` is deliberately EXCLUDED here
          // — it is modelled as resumable (the reducer's `resume_from_ended`
          // re-opens it and ProjectScopingPage's `terminalVoiceEnd` excludes it),
          // so it falls through to the default resumable ended surface below,
          // consistent with the rest of this PR (PR #1054 P2).
          <ConversationEndedState reason="complete" onReset={() => lifecycle.reset()} />
        ) : (
          <ConversationEndedState onReset={() => lifecycle.reset()} />
        )
      ) : (
        <ConversationInputBar
          inputReady={inputReady}
          // Text input is always enabled — `useTextConversation.sendMessage`
          // lazy-mints `conversation_id` and the server's `/message` route
          // creates the mapping on demand. The user can type at any time;
          // there's no "loading" state to wait through.
          textRequiresSessionReady={false}
          onSend={sendMessage}
          busy={isThinking || (isResponding ?? false)}
          inputMode={inputMode}
          onSwitchToVoice={handleSwitchToVoice}
          onSwitchToText={handleSwitchToText}
          onStartVoiceFromIdle={handleStartVoiceFromIdle}
          voiceState={voiceState}
          idlePrompting={idlePrompting}
          sessionCapWarning={sessionCapWarning && !lockScopeRequested}
          onExtendSession={extendSession}
          onLockScope={handleLockScope}
          textStatusUpdate={textStatusUpdate}
          room={room}
          livekitUrl={livekitUrl}
          micBlocked={micBlocked}
          micErrorKind={micErrorKind}
          paused={paused}
          pauseReason={pauseReason}
          voiceError={error}
          voiceStartedAt={voiceStartedAt}
          pausedMs={pausedMs}
          onPause={pauseVoice}
          onResume={resumeVoice}
          onEndCall={disconnectVoice}
          expanded={expandedProp}
          onToggleExpand={onToggleExpandProp}
          mode={shellMode}
          onToggleMode={onToggleShellMode}
          pinnedVoiceChrome={pinnedVoiceChrome}
        />
      )}
    </div>
  );
}
